import React from 'react';
import { View, StyleSheet, Dimensions, Platform } from 'react-native';
import Animated, {
  useAnimatedStyle,
  interpolate,
  runOnJS,
  withTiming,
  Easing,
} from 'react-native-reanimated';
import type { SharedValue } from 'react-native-reanimated';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { DragHandle } from './DragHandle';

const SCREEN_HEIGHT = Dimensions.get('window').height;
const DISMISS_THRESHOLD = 150;
const VELOCITY_THRESHOLD = 500;
const SNAP_BACK = { duration: 200, easing: Easing.out(Easing.cubic) };

interface SheetContentProps {
  children: React.ReactNode;
  translateY: SharedValue<number>;
  backdropOpacity: SharedValue<number>;
  maxBackdropOpacity: number;
  onDismiss: () => void;
  enableGestureDismiss: boolean;
  /** When true, collapses the bottom safe-area paddingBottom — the home indicator
   *  is hidden behind the keyboard, so reserving space for it just creates an
   *  empty gap between the footer and the keyboard. */
  keyboardVisible?: boolean;
}

export const SheetContent: React.FC<SheetContentProps> = ({
  children,
  translateY,
  backdropOpacity,
  maxBackdropOpacity,
  onDismiss,
  enableGestureDismiss,
  keyboardVisible = false,
}) => {
  const { colors } = useThemeColors();
  const insets = useSafeAreaInsets();
  const isWeb = Platform.OS === 'web';

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateY: translateY.value }],
  }));

  const gesture = Gesture.Pan()
    .enabled(enableGestureDismiss && !isWeb)
    .activeOffsetY(5)
    .failOffsetX([-30, 30])
    .onUpdate((e) => {
      'worklet';
      const clamped = Math.max(0, e.translationY);
      translateY.value = clamped;
      backdropOpacity.value = interpolate(
        clamped,
        [0, DISMISS_THRESHOLD],
        [maxBackdropOpacity, 0],
        'clamp',
      );
    })
    .onEnd((e) => {
      'worklet';
      if (
        e.translationY > DISMISS_THRESHOLD ||
        e.velocityY > VELOCITY_THRESHOLD
      ) {
        translateY.value = withTiming(SCREEN_HEIGHT, { duration: 200 });
        backdropOpacity.value = withTiming(0, { duration: 200 });
        runOnJS(onDismiss)();
      } else {
        translateY.value = withTiming(0, SNAP_BACK);
        backdropOpacity.value = withTiming(maxBackdropOpacity, SNAP_BACK);
      }
    });

  return (
    <View style={styles.container} pointerEvents="box-none">
      <GestureDetector gesture={gesture}>
        <Animated.View
          style={[
            styles.sheet,
            { backgroundColor: colors.bg.modal, borderColor: colors.card.border, paddingBottom: keyboardVisible ? 0 : Math.max(insets.bottom, 24) },
            animatedStyle,
          ]}
        >
          {enableGestureDismiss && !isWeb && <DragHandle />}
          {children}
        </Animated.View>
      </GestureDetector>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  sheet: {
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    borderWidth: 1,
    borderBottomWidth: 0,
    maxHeight: '90%',
    overflow: 'hidden',
  },
});
